import { useEffect, useState } from 'react';
import type { LiveOdds } from '@pons/contracts';
import {
  parseStake,
  clampStake,
  MAX_POINTS,
  normalizeChoicePayload,
} from '@pons/contracts';
import { Money } from '@pons/contracts/money';
import type { ArenaRound } from './model';
import { choiceApiPath } from '../choice-api';

export function useLiveOdds(
  round: Pick<ArenaRound, 'id' | 'odds'>,
  active: boolean,
  balance: string | null = MAX_POINTS,
) {
  const [draft, setDraft] = useState('100.00');
  const amount =
    balance !== null &&
    draft !== '' &&
    draft !== '.' &&
    new Money(draft).gt(balance)
      ? clampStake(draft, balance)
      : draft;
  const setAmount = (value: string) => {
    if (value !== '' && !/^\d{0,19}(?:\.\d{0,2})?$/.test(value)) return;
    const plain = value.replace(/^0+(?=\d)/, '');
    setDraft(
      balance !== null &&
        plain !== '' &&
        plain !== '.' &&
        new Money(plain).gt(balance)
        ? clampStake(plain, balance)
        : plain,
    );
  };
  useEffect(() => {
    if (amount !== draft) setDraft(amount);
  }, [amount, draft]);
  const finishAmount = () => setDraft(clampStake(amount, balance));
  const [remote, setRemote] = useState<{ id: string; quote: LiveOdds } | null>(
    null,
  );
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [reloading, setReloading] = useState(false);
  const hasOdds = !!round.odds,
    marketVersion = round.odds?.version,
    defaultAmount = round.odds?.amount;
  const normalized =
    balance !== null && new Money(balance).gte(2) ? parseStake(amount) : null;
  const baseline = normalized === round.odds?.amount ? round.odds : null;
  const freshRemote =
    remote?.id === round.id &&
    remote.quote.amount === normalized &&
    remote.quote.version >= (round.odds?.version ?? 0)
      ? remote.quote
      : null;
  const quote = reloading
    ? null
    : freshRemote && (!baseline || freshRemote.version >= baseline.version)
      ? freshRemote
      : baseline;
  useEffect(() => {
    if (
      !active ||
      !hasOdds ||
      !normalized ||
      (normalized === defaultAmount && revision === 0)
    )
      return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void fetch(
        choiceApiPath(
          (round.id.startsWith('game-')
            ? '/api/game/rounds/'
            : '/api/rounds/') +
            encodeURIComponent(round.id) +
            '/odds?amount=' +
            normalized,
        ),
        { signal: controller.signal, cache: 'no-store' },
      )
        .then(async (response) => {
          const body = normalizeChoicePayload(await response.json());
          if (!response.ok) throw Error(body.error || 'Odds unavailable.');
          if (
            body.model !== 'lmsr-v1-floor2' ||
            body.unit !== 'virtual' ||
            body.amount !== normalized
          )
            throw Error('Invalid quote received.');
          if (!controller.signal.aborted) {
            setRemote({ id: round.id, quote: body });
            setReloading(false);
            setError('');
          }
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setError(
              error instanceof Error ? error.message : 'Odds unavailable.',
            );
        });
    }, 120);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    round.id,
    marketVersion,
    defaultAmount,
    hasOdds,
    normalized,
    active,
    revision,
  ]);
  return {
    amount,
    setAmount,
    finishAmount,
    quote,
    error: normalized ? error : '',
    reload: () => {
      setError('');
      setReloading(true);
      setRemote(null);
      setRevision((n) => n + 1);
    },
  };
}
